/**
 * Applies an admin-published game theme (colours + decorations) on top of player Settings.
 * Player can opt out via Settings → Show Admin Theme (default on).
 */
import { fetchActiveGameTheme } from "../lib/cloud";
import {
  emptyThemeData,
  normalizeThemeData,
  isThemeScreenVisible,
  themeMusicIsActive,
  type ThemeColors,
  type ThemeData,
  type ThemeDecoration,
  type ThemeMusicData,
} from "./theme-types";
import { ANIM_FRAME_MS } from "./shop-creator-fx";
import { clearThemeParticleEmitters, syncThemeParticleEmitters } from "./theme-particles";
import { getPlayerThemeParticleDecorations } from "./theme-cosmetic";

const LAYER_ID = "gameThemeDecorLayer";
const BG_LAYER_ID = "gameThemeBgDecorLayer";
const SIDEBAR_LAYER_ID = "gameThemeSidebarDecorLayer";
const OPT_IN_KEY = "settingShowAdminTheme";
const THEME_DEVICE_PREVIEW_KEY = "cgThemePreviewPayload";

let publishedTheme: ThemeData | null = null;
let previewTheme: ThemeData | null = null;
let playerOptIn = true;
let animTimer = 0;
let animFrameIndex = 0;
let editMode = false;
let selectedDecorId: string | null = null;
let onDecorEdit: ((id: string, patch: Partial<ThemeDecoration> | "delete") => void) | null = null;
let attachSyncRaf = 0;
let bodyClassObserver: MutationObserver | null = null;
let lastBodyThemeSig = "";
/** While >0, skip full decor remounts (anim tick) so drag/resize is not reset. */
let decorInteractLock = 0;

/** Restore player Settings CSS for channels marked Don't Change. */
export type ThemePlayerColorChannels = {
  background: boolean;
  balls: boolean;
  buttons: boolean;
  text: boolean;
};
let restorePlayerColorChannels: ((channels: ThemePlayerColorChannels) => void) | null = null;

export function setThemePlayerColorRestorer(
  fn: ((channels: ThemePlayerColorChannels) => void) | null,
): void {
  restorePlayerColorChannels = fn;
}

let themeMusicPlaybackHook: (() => void) | null = null;

/** Called when theme music payload may have changed (preview / publish / opt-in). */
export function setThemeMusicPlaybackHook(fn: (() => void) | null): void {
  themeMusicPlaybackHook = fn;
}

function notifyThemeMusicMaybeChanged(): void {
  try {
    themeMusicPlaybackHook?.();
  } catch {
    /* ignore */
  }
}

/** Active theme music that should replace arcade tracks (respects Show Admin Theme). */
export function getEffectiveThemeMusic(): ThemeMusicData | null {
  const theme = effectiveTheme();
  if (!theme?.music || !themeMusicIsActive(theme.music)) return null;
  return theme.music;
}

/** Update only the preview theme's music (avoids remounting decorations while composing). */
export function patchThemePreviewMusic(music: ThemeMusicData): void {
  if (!previewTheme) return;
  const normalized = normalizeThemeData({ ...previewTheme, music });
  previewTheme = normalized;
  notifyThemeMusicMaybeChanged();
}

export function getShowAdminThemeSetting(): boolean {
  try {
    const v = localStorage.getItem(OPT_IN_KEY);
    if (v === null) return true;
    return v === "1" || v === "true";
  } catch {
    return true;
  }
}

export function setShowAdminThemeSetting(on: boolean): void {
  playerOptIn = on;
  try {
    localStorage.setItem(OPT_IN_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
  refreshGameThemePresentation();
  notifyThemeMusicMaybeChanged();
}

export function initShowAdminThemeFromStorage(): void {
  playerOptIn = getShowAdminThemeSetting();
}

/** iframe device-preview bootstrap (`?theme_device_preview=1`). */
export function bootstrapThemeDevicePreviewFrame(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("theme_device_preview") !== "1") return false;
  document.documentElement.classList.add("theme-device-preview-frame");
  document.body.classList.add("theme-device-preview-frame");
  try {
    const raw = sessionStorage.getItem(THEME_DEVICE_PREVIEW_KEY);
    if (raw) {
      previewTheme = normalizeThemeData(JSON.parse(raw) as ThemeData);
      refreshGameThemePresentation();
    }
  } catch {
    /* ignore */
  }
  return true;
}

function effectiveTheme(): ThemeData | null {
  if (previewTheme) return previewTheme;
  if (!playerOptIn) return null;
  return publishedTheme;
}

export function setThemePreviewData(data: ThemeData | null): void {
  previewTheme = data ? normalizeThemeData(data) : null;
  refreshGameThemePresentation();
  notifyThemeMusicMaybeChanged();
}

export function getThemePreviewData(): ThemeData | null {
  return previewTheme;
}

export function isThemePreviewActive(): boolean {
  return !!previewTheme;
}

export function setThemeDecorEditMode(
  on: boolean,
  handler?: ((id: string, patch: Partial<ThemeDecoration> | "delete") => void) | null,
): void {
  editMode = on;
  onDecorEdit = on ? handler || null : null;
  if (!on) selectedDecorId = null;
  document.body.classList.toggle("theme-decor-edit", on);
  syncDecorLayers();
}

export function getSelectedThemeDecorId(): string | null {
  return selectedDecorId;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

function mixRgb(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
  t: number,
) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

/** Apply theme colour overrides onto :root CSS variables (call after applySettings). */
export function applyGameThemeColorOverrides(): void {
  const theme = effectiveTheme();
  const root = document.documentElement;
  document.body.classList.toggle("admin-theme-active", !!theme);
  document.body.classList.toggle("admin-theme-preview", !!previewTheme);

  if (!theme) {
    root.style.removeProperty("--admin-theme-text-from");
    root.style.removeProperty("--admin-theme-text-to");
    root.style.removeProperty("--ball-color-to");
    root.style.removeProperty("--menu-bg-to");
    root.style.removeProperty("--game-bg-to");
    document.body.classList.remove("admin-theme-balls-gradient");
    document.body.classList.remove("admin-theme-bg-gradient");
    document.body.classList.remove("admin-theme-text-gradient");
    return;
  }

  const c = theme.colors;
  const skipBg = !!c.dontChangeBackground;
  const skipBalls = !!c.dontChangeBalls;
  const skipButtons = !!c.dontChangeButtons;
  const skipText = !!c.dontChangeText;

  if (skipBg || skipBalls || skipButtons || skipText) {
    restorePlayerColorChannels?.({
      background: skipBg,
      balls: skipBalls,
      buttons: skipButtons,
      text: skipText,
    });
  }

  document.body.classList.toggle(
    "admin-theme-text-gradient",
    !skipText && !!c.textGradient,
  );

  if (!skipBg) {
    root.style.setProperty("--menu-bg-raw", c.backgroundColor);
    root.style.setProperty("--game-bg-raw", c.backgroundColor);
    root.style.setProperty("--menu-bg-color", c.backgroundColor);
    root.style.setProperty("--game-bg-color", c.backgroundColor);
    root.style.setProperty("--bg-color", c.backgroundColor);
    if (c.backgroundGradient) {
      root.style.setProperty("--menu-bg-to", c.backgroundGradientTo);
      root.style.setProperty("--game-bg-to", c.backgroundGradientTo);
      document.body.classList.add("admin-theme-bg-gradient");
    } else {
      root.style.removeProperty("--menu-bg-to");
      root.style.removeProperty("--game-bg-to");
      document.body.classList.remove("admin-theme-bg-gradient");
    }
  } else {
    root.style.removeProperty("--menu-bg-to");
    root.style.removeProperty("--game-bg-to");
    document.body.classList.remove("admin-theme-bg-gradient");
  }

  if (!skipButtons) {
    root.style.setProperty("--btn-main-bg", c.buttonsColor);
    root.style.setProperty("--btn-main-glow", c.buttonsColor);
    root.style.setProperty("--btn-green-bg", c.buttonsColor);
    root.style.setProperty("--btn-green-glow", c.buttonsColor);
    root.style.setProperty("--home-start-glow", c.buttonsColor);
    if (c.buttonsGradient) {
      const from = hexToRgb(c.buttonsColor);
      const to = hexToRgb(c.buttonsGradientTo);
      const mid = mixRgb(from, to, 0.45);
      const edge = mixRgb(to, { r: 0, g: 0, b: 0 }, 0.35);
      root.style.setProperty("--btn-sky-light", c.buttonsColor);
      root.style.setProperty("--btn-sky-mid", rgbToHex(mid.r, mid.g, mid.b));
      root.style.setProperty("--btn-sky-edge", rgbToHex(edge.r, edge.g, edge.b));
      root.style.setProperty("--btn-sky-edge-hover", c.buttonsGradientTo);
    } else {
      root.style.setProperty("--btn-sky-light", c.buttonsColor);
      root.style.setProperty("--btn-sky-mid", c.buttonsColor);
      root.style.setProperty("--btn-sky-edge", c.buttonsColor);
      root.style.setProperty("--btn-sky-edge-hover", c.buttonsColor);
    }
  }

  if (!skipText) {
    root.style.setProperty("--text-color", c.textColor);
    root.style.setProperty("--home-title-glow", c.textColor);
    if (c.textGradient) {
      root.style.setProperty("--admin-theme-text-from", c.textColor);
      root.style.setProperty("--admin-theme-text-to", c.textGradientTo);
    } else {
      root.style.removeProperty("--admin-theme-text-from");
      root.style.removeProperty("--admin-theme-text-to");
    }
  } else {
    root.style.removeProperty("--admin-theme-text-from");
    root.style.removeProperty("--admin-theme-text-to");
  }

  if (!skipBalls) {
    root.style.setProperty("--ball-color", c.ballsColor);
    if (c.ballsGradient) {
      root.style.setProperty("--ball-color-to", c.ballsGradientTo);
      document.body.classList.add("admin-theme-balls-gradient");
    } else {
      root.style.removeProperty("--ball-color-to");
      document.body.classList.remove("admin-theme-balls-gradient");
    }
  } else {
    root.style.removeProperty("--ball-color-to");
    document.body.classList.remove("admin-theme-balls-gradient");
  }
}

function ensureLayer(id: string, parent: HTMLElement, className: string): HTMLElement {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("div");
    el.id = id;
    el.className = className;
    el.setAttribute("aria-hidden", "true");
    parent.appendChild(el);
  } else if (el.parentElement !== parent) {
    parent.appendChild(el);
  }
  el.className = className;
  return el;
}

function decorationShowsOnViewport(dec: ThemeDecoration, playing: boolean): boolean {
  if (dec.screens.includes("background")) return !playing;
  if (dec.screens.includes("all")) return !playing;
  // Only the listed screens — never a catch-all for static art.
  for (const key of dec.screens) {
    if (key === "sidebar") continue;
    if (isThemeScreenVisible(key, playing)) return true;
  }
  return false;
}

/** Sidebar-anchored art: only while its target screen(s) are open (or pure sidebar anim). */
function decorationShowsOnSidebar(dec: ThemeDecoration, playing: boolean): boolean {
  if (dec.screens.includes("sidebar") && dec.screens.length === 1) return !playing;
  if (dec.screens.includes("all") || dec.screens.includes("background")) return !playing;
  for (const key of dec.screens) {
    if (key === "sidebar") continue;
    if (isThemeScreenVisible(key, playing)) return true;
  }
  return false;
}

function bodyThemeVisibilitySig(): string {
  return [
    document.body.classList.contains("game-active") ? "1" : "0",
    document.body.classList.contains("challenge-lobby") ? "1" : "0",
    document.body.classList.contains("challenge-active") ? "1" : "0",
  ].join("");
}

function ensureBodyThemeObserver(): void {
  if (bodyClassObserver || typeof MutationObserver === "undefined" || typeof document === "undefined") {
    return;
  }
  lastBodyThemeSig = bodyThemeVisibilitySig();
  bodyClassObserver = new MutationObserver(() => {
    if (decorInteractLock > 0) return;
    const sig = bodyThemeVisibilitySig();
    if (sig === lastBodyThemeSig) return;
    lastBodyThemeSig = sig;
    refreshGameThemePresentation();
  });
  bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
}

function applyAttachLayout(wrap: HTMLElement, dec: ThemeDecoration): boolean {
  const sel = dec.attachSelector;
  if (!sel) return false;
  let target: Element | null = null;
  try {
    target = document.querySelector(sel);
  } catch {
    target = null;
  }
  if (!(target instanceof HTMLElement) || !target.isConnected) {
    wrap.style.visibility = "hidden";
    return true;
  }
  const r = target.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) {
    wrap.style.visibility = "hidden";
    return true;
  }
  const x = Number(wrap.dataset.attachX ?? dec.xPct);
  const y = Number(wrap.dataset.attachY ?? dec.yPct);
  const w = Number(wrap.dataset.attachW ?? dec.widthPct);
  const h = Number(wrap.dataset.attachH ?? dec.heightPct);
  const rotDeg = Number(wrap.dataset.rotationDeg || dec.rotationDeg || 0);
  wrap.style.visibility = "visible";
  // Pixel box from getBoundingClientRect so CSS hover/active transforms (scale) are followed.
  wrap.style.left = `${r.left + (x / 100) * r.width}px`;
  wrap.style.top = `${r.top + (y / 100) * r.height}px`;
  wrap.style.width = `${(w / 100) * r.width}px`;
  wrap.style.height = `${(h / 100) * r.height}px`;
  wrap.style.transform = rotDeg ? `rotate(${rotDeg}deg)` : "";
  return true;
}

function syncAttachedDecorations(): void {
  const nodes = document.querySelectorAll<HTMLElement>(
    ".gameThemeDecorLayer .gameThemeDecor[data-theme-attach], .gameThemeBgDecorLayer .gameThemeDecor[data-theme-attach]",
  );
  if (!nodes.length) {
    if (attachSyncRaf) {
      cancelAnimationFrame(attachSyncRaf);
      attachSyncRaf = 0;
    }
    return;
  }
  for (const wrap of nodes) {
    if (wrap.classList.contains("gameThemeDecor--inHost")) continue;
    const sel = wrap.getAttribute("data-theme-attach");
    if (!sel) continue;
    const decLike: ThemeDecoration = {
      id: wrap.dataset.themeDecorId || "",
      kind: "static",
      imageData: "",
      anchor: "viewport",
      xPct: Number(wrap.dataset.attachX || 0),
      yPct: Number(wrap.dataset.attachY || 0),
      widthPct: Number(wrap.dataset.attachW || 12),
      heightPct: Number(wrap.dataset.attachH || 12),
      screens: [],
      attachSelector: sel,
      rotationDeg: Number(wrap.dataset.rotationDeg || 0) || undefined,
    };
    applyAttachLayout(wrap, decLike);
  }
}

function startAttachSyncLoop(): void {
  if (attachSyncRaf) return;
  const tick = () => {
    syncAttachedDecorations();
    if (!document.querySelector(".gameThemeDecor[data-theme-attach]")) {
      attachSyncRaf = 0;
      return;
    }
    attachSyncRaf = requestAnimationFrame(tick);
  };
  attachSyncRaf = requestAnimationFrame(tick);
}

function clearInHostDecorations(): void {
  document.querySelectorAll(".gameThemeDecor--inHost").forEach((el) => el.remove());
  document.querySelectorAll(".gameThemeDecorAttachParent").forEach((el) => {
    el.classList.remove("gameThemeDecorAttachParent");
  });
}

function resolveAttachHost(selector: string): HTMLElement | null {
  try {
    const t = document.querySelector(selector);
    return t instanceof HTMLElement && t.isConnected ? t : null;
  } catch {
    return null;
  }
}

function patchPreviewDecoration(id: string, patch: Partial<ThemeDecoration>): void {
  if (!previewTheme) return;
  previewTheme = {
    ...previewTheme,
    decorations: previewTheme.decorations.map((d) => (d.id === id ? { ...d, ...patch } : d)),
  };
}

function renderDecorationEl(
  dec: ThemeDecoration,
  frameSrc: string,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className =
    "gameThemeDecor" +
    (editMode ? " gameThemeDecor--editable" : "") +
    (dec.attachSelector ? " gameThemeDecor--attached" : "");
  if (selectedDecorId === dec.id) wrap.classList.add("gameThemeDecor--selected");
  wrap.dataset.themeDecorId = dec.id;
  wrap.style.opacity = String(dec.opacity ?? 1);
  if (dec.zIndex != null) wrap.style.zIndex = String(dec.zIndex);

  if (dec.attachSelector) {
    wrap.dataset.themeAttach = dec.attachSelector;
    wrap.setAttribute("data-theme-attach", dec.attachSelector);
    wrap.dataset.attachX = String(dec.xPct);
    wrap.dataset.attachY = String(dec.yPct);
    wrap.dataset.attachW = String(dec.widthPct);
    wrap.dataset.attachH = String(dec.heightPct);
    applyAttachLayout(wrap, dec);
  } else {
    wrap.style.left = `${dec.xPct}%`;
    wrap.style.top = `${dec.yPct}%`;
    wrap.style.width = `${dec.widthPct}%`;
    wrap.style.height = `${dec.heightPct}%`;
  }

  const rotDeg = ((Math.round(dec.rotationDeg || 0) % 360) + 360) % 360;
  if (!dec.attachSelector) {
    wrap.style.transform = rotDeg ? `rotate(${rotDeg}deg)` : "";
  }
  wrap.dataset.rotationDeg = String(rotDeg);

  const img = document.createElement("img");
  img.src = frameSrc;
  img.alt = "";
  img.draggable = false;
  wrap.appendChild(img);

  if (editMode && onDecorEdit) {
    wrap.style.pointerEvents = "auto";
    const handle = document.createElement("div");
    handle.className = "gameThemeDecorHandle";
    handle.title = "Resize";
    wrap.appendChild(handle);

    const rotBtn = document.createElement("button");
    rotBtn.type = "button";
    rotBtn.className = "gameThemeDecorRotate";
    rotBtn.textContent = "↻";
    rotBtn.title = "Hold and drag sideways to rotate";
    rotBtn.onpointerdown = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      selectedDecorId = dec.id;
      document.querySelectorAll(".gameThemeDecor--selected").forEach((el) => {
        el.classList.remove("gameThemeDecor--selected");
      });
      wrap.classList.add("gameThemeDecor--selected");
      const startX = ev.clientX;
      const startRot = ((Math.round(dec.rotationDeg || 0) % 360) + 360) % 360;
      const pointerId = ev.pointerId;
      decorInteractLock += 1;
      try {
        rotBtn.setPointerCapture(pointerId);
      } catch {
        /* ignore */
      }
      const onMove = (e: PointerEvent) => {
        const dx = e.clientX - startX;
        const next = (((startRot + dx * 0.55) % 360) + 360) % 360;
        wrap.style.transform = `rotate(${next}deg)`;
        wrap.dataset.liveRot = String(next);
        wrap.dataset.rotationDeg = String(next);
      };
      const onUp = () => {
        rotBtn.removeEventListener("pointermove", onMove);
        rotBtn.removeEventListener("pointerup", onUp);
        rotBtn.removeEventListener("pointercancel", onUp);
        try {
          rotBtn.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
        decorInteractLock = Math.max(0, decorInteractLock - 1);
        const next = Number(wrap.dataset.liveRot);
        if (Number.isFinite(next)) {
          patchPreviewDecoration(dec.id, { rotationDeg: next });
          onDecorEdit?.(dec.id, { rotationDeg: next });
        }
      };
      rotBtn.addEventListener("pointermove", onMove);
      rotBtn.addEventListener("pointerup", onUp);
      rotBtn.addEventListener("pointercancel", onUp);
    };
    wrap.appendChild(rotBtn);

    const del = document.createElement("button");
    del.type = "button";
    del.className = "gameThemeDecorDelete";
    del.textContent = "×";
    del.title = "Delete";
    del.onclick = (ev) => {
      ev.stopPropagation();
      onDecorEdit?.(dec.id, "delete");
    };
    wrap.appendChild(del);

    let mode: "move" | "resize" | null = null;
    let startX = 0;
    let startY = 0;
    let orig = { x: dec.xPct, y: dec.yPct, w: dec.widthPct, h: dec.heightPct };
    let live = { x: dec.xPct, y: dec.yPct, w: dec.widthPct, h: dec.heightPct };
    let box = { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

    const resolveBox = () => {
      if (dec.attachSelector) {
        const t = resolveAttachHost(dec.attachSelector);
        if (t) {
          const r = t.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            box = { left: r.left, top: r.top, width: r.width, height: r.height };
            return;
          }
        }
      }
      const parent = wrap.parentElement;
      if (parent) {
        const r = parent.getBoundingClientRect();
        box = { left: r.left, top: r.top, width: r.width, height: r.height };
      }
    };

    const applyLiveBox = (xPct: number, yPct: number, widthPct: number, heightPct: number) => {
      live = { x: xPct, y: yPct, w: widthPct, h: heightPct };
      wrap.dataset.attachX = String(xPct);
      wrap.dataset.attachY = String(yPct);
      wrap.dataset.attachW = String(widthPct);
      wrap.dataset.attachH = String(heightPct);
      if (dec.attachSelector) {
        applyAttachLayout(wrap, { ...dec, xPct, yPct, widthPct, heightPct });
      } else {
        wrap.style.left = `${xPct}%`;
        wrap.style.top = `${yPct}%`;
        wrap.style.width = `${widthPct}%`;
        wrap.style.height = `${heightPct}%`;
      }
    };

    const onMove = (ev: PointerEvent) => {
      if (!mode) return;
      resolveBox();
      const dx = ((ev.clientX - startX) / Math.max(1, box.width)) * 100;
      const dy = ((ev.clientY - startY) / Math.max(1, box.height)) * 100;
      if (mode === "move") {
        const xPct = dec.attachSelector
          ? orig.x + dx
          : Math.max(-20, Math.min(100, orig.x + dx));
        const yPct = dec.attachSelector
          ? orig.y + dy
          : Math.max(-20, Math.min(100, orig.y + dy));
        applyLiveBox(xPct, yPct, orig.w, orig.h);
      } else {
        const widthPct = Math.max(4, Math.min(dec.attachSelector ? 300 : 100, orig.w + dx));
        const heightPct = Math.max(4, Math.min(dec.attachSelector ? 300 : 100, orig.h + dy));
        applyLiveBox(orig.x, orig.y, widthPct, heightPct);
      }
    };
    const onUp = () => {
      const was = mode;
      mode = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      decorInteractLock = Math.max(0, decorInteractLock - 1);
      if (was === "move") {
        const patch = { xPct: live.x, yPct: live.y };
        patchPreviewDecoration(dec.id, patch);
        onDecorEdit?.(dec.id, patch);
      } else if (was === "resize") {
        const patch = { widthPct: live.w, heightPct: live.h };
        patchPreviewDecoration(dec.id, patch);
        onDecorEdit?.(dec.id, patch);
      }
    };

    wrap.onpointerdown = (ev) => {
      if ((ev.target as HTMLElement).closest(".gameThemeDecorDelete, .gameThemeDecorRotate")) return;
      selectedDecorId = dec.id;
      document.querySelectorAll(".gameThemeDecor--selected").forEach((el) => {
        el.classList.remove("gameThemeDecor--selected");
      });
      wrap.classList.add("gameThemeDecor--selected");
      const isHandle = !!(ev.target as HTMLElement).closest(".gameThemeDecorHandle");
      mode = isHandle ? "resize" : "move";
      startX = ev.clientX;
      startY = ev.clientY;
      const theme = effectiveTheme();
      const cur = theme?.decorations.find((d) => d.id === dec.id);
      orig = {
        x: cur?.xPct ?? dec.xPct,
        y: cur?.yPct ?? dec.yPct,
        w: cur?.widthPct ?? dec.widthPct,
        h: cur?.heightPct ?? dec.heightPct,
      };
      live = { ...orig };
      resolveBox();
      decorInteractLock += 1;
      ev.preventDefault();
      ev.stopPropagation();
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    };
  }

  return wrap;
}

function syncDecorLayers(): void {
  ensureBodyThemeObserver();
  const theme = effectiveTheme();
  const viewportHost = document.body;
  const sideNav = document.getElementById("sideNav");

  const viewportLayer = ensureLayer(LAYER_ID, viewportHost, "gameThemeDecorLayer");
  const bgLayer = ensureLayer(BG_LAYER_ID, viewportHost, "gameThemeBgDecorLayer");
  let sidebarLayer: HTMLElement | null = null;
  if (sideNav) {
    sidebarLayer = ensureLayer(SIDEBAR_LAYER_ID, sideNav, "gameThemeSidebarDecorLayer");
  }

  clearInHostDecorations();
  viewportLayer.replaceChildren();
  bgLayer.replaceChildren();
  sidebarLayer?.replaceChildren();

  if (!theme) {
    viewportLayer.classList.add("hidden");
    bgLayer.classList.add("hidden");
    sidebarLayer?.classList.add("hidden");
    if (attachSyncRaf) {
      cancelAnimationFrame(attachSyncRaf);
      attachSyncRaf = 0;
    }
    return;
  }

  const playing = document.body.classList.contains("game-active");
  let anyView = false;
  let anyBg = false;
  let anySide = false;
  let anyAttachedFixed = false;

  for (const dec of theme.decorations) {
    if (dec.kind === "particles") continue;
    const frames =
      dec.kind === "animated" && dec.frames?.length ? dec.frames : [dec.imageData];
    const src = frames[animFrameIndex % frames.length] || dec.imageData;

    if (dec.attachSelector) {
      if (!decorationShowsOnViewport(dec, playing)) continue;
      // Always paint on the fixed layer and sync from getBoundingClientRect so
      // hover/active transforms on the host (e.g. START scale) are followed.
      const el = renderDecorationEl(dec, src);
      viewportLayer.appendChild(el);
      anyView = true;
      anyAttachedFixed = true;
      continue;
    }

    if (dec.anchor === "sidebar") {
      if (!sidebarLayer) continue;
      if (!decorationShowsOnSidebar(dec, playing)) continue;
      sidebarLayer.appendChild(renderDecorationEl(dec, src));
      anySide = true;
      continue;
    }

    if (!decorationShowsOnViewport(dec, playing)) continue;

    const el = renderDecorationEl(dec, src);
    if (dec.layer === "back" || dec.screens.includes("background")) {
      bgLayer.appendChild(el);
      anyBg = true;
    } else {
      viewportLayer.appendChild(el);
      anyView = true;
    }
  }

  viewportLayer.classList.toggle("hidden", !viewportLayer.childElementCount);
  bgLayer.classList.toggle("hidden", !anyBg);
  sidebarLayer?.classList.toggle("hidden", !anySide);
  if (anyAttachedFixed) startAttachSyncLoop();
  else if (attachSyncRaf) {
    cancelAnimationFrame(attachSyncRaf);
    attachSyncRaf = 0;
  }
}

function startAnimLoop(): void {
  if (animTimer) return;
  animTimer = window.setInterval(() => {
    if (decorInteractLock > 0) return;
    const theme = effectiveTheme();
    const hasAnim = !!theme?.decorations.some((d) => d.kind === "animated" && (d.frames?.length || 0) > 1);
    if (!hasAnim) return;
    animFrameIndex = (animFrameIndex + 1) % 10;
    // Swap frame images in place — avoid full remount (resets drag / selection).
    let swapped = false;
    for (const dec of theme?.decorations || []) {
      if (dec.kind !== "animated" || !dec.frames?.length) continue;
      const src = dec.frames[animFrameIndex % dec.frames.length] || dec.imageData;
      const wrap = document.querySelector<HTMLElement>(
        `.gameThemeDecor[data-theme-decor-id="${CSS.escape(dec.id)}"]`,
      );
      const img = wrap?.querySelector("img");
      if (img && img.src !== src) {
        img.src = src;
        swapped = true;
      }
    }
    if (!swapped) syncDecorLayers();
  }, ANIM_FRAME_MS);
}

export function refreshGameThemePresentation(): void {
  applyGameThemeColorOverrides();
  syncDecorLayers();
  startAnimLoop();
  const theme = effectiveTheme();
  const adminDecs = theme?.decorations ?? [];
  const adminHasParticles = adminDecs.some((d) => d.kind === "particles");

  // Admin Theme Creator / live admin theme particles always win while the theme is effective
  // (published + Show Admin Theme, or Theme Creator preview). Inventory Theme particles stay off.
  if (adminHasParticles) {
    void syncThemeParticleEmitters(adminDecs);
    return;
  }

  // Inside Theme Creator preview with no particle system on the draft: do not show inventory particles.
  if (previewTheme) {
    clearThemeParticleEmitters();
    return;
  }

  const playerParticles = getPlayerThemeParticleDecorations();
  if (playerParticles.length) {
    void syncThemeParticleEmitters(playerParticles);
  } else {
    clearThemeParticleEmitters();
  }
}

/**
 * Modes where ball colour is gameplay identity — Theme / Admin must not tint.
 * Color Rush / Hunter / Hook: colour conveys threat or match rules.
 * Counter: target vs idle colours are the puzzle.
 */
const THEME_BALL_COLOR_LOCKED = new Set([
  "colorrush",
  "hunter",
  "hook",
  "counter",
]);

/**
 * Modes where obstacle ball colour is cosmetic (Settings / player Theme / Admin Theme).
 * AIM is included: only low-opacity (non-golden) distractors should use theme paint —
 * callers must skip the golden target via modeAllowsThemeBallColorForBall.
 */
const THEME_BALL_COLOR_MODES = new Set([
  "normal",
  "wasd",
  "superdot",
  "progression",
  "roulette",
  "sandbox",
  "speedrun",
  "highway",
  "darkness",
  "mirror",
  "delay",
  "snake",
  "combo",
  "heat",
  "pop",
  "tilt",
  "orbit",
  "aim",
  "penalty",
  "multiplayer",
]);

/** False for palette-locked modes (Color Rush, Hunter, Hook, Counter). AIM returns true — filter golden at draw time. */
export function modeAllowsThemeBallColor(mode: string | null | undefined): boolean {
  if (!mode || THEME_BALL_COLOR_LOCKED.has(mode)) return false;
  return THEME_BALL_COLOR_MODES.has(mode);
}

/**
 * Per-ball theme tint. AIM golden targets keep gold; other AIM balls (low opacity) may use theme.
 */
export function modeAllowsThemeBallColorForBall(
  mode: string | null | undefined,
  opts?: { isAimGolden?: boolean },
): boolean {
  if (!modeAllowsThemeBallColor(mode)) return false;
  if (mode === "aim" && opts?.isAimGolden) return false;
  return true;
}

/** Theme balls paint for eligible modes; null when theme inactive or mode is palette-locked. */
export function getThemeBallsPaint(mode: string | null | undefined): {
  color: string | null;
  gradientTo: string | null;
} {
  if (!modeAllowsThemeBallColor(mode)) return { color: null, gradientTo: null };
  const theme = effectiveTheme();
  if (!theme || theme.colors.dontChangeBalls) return { color: null, gradientTo: null };
  return {
    color: theme.colors.ballsColor || null,
    gradientTo: theme.colors.ballsGradient ? theme.colors.ballsGradientTo : null,
  };
}

/** Re-apply after player Settings so theme overrides win when opted in / previewing. */
export function afterPlayerSettingsApplied(): void {
  refreshGameThemePresentation();
}

export async function loadPublishedGameTheme(): Promise<void> {
  try {
    const row = await fetchActiveGameTheme();
    publishedTheme = row ? normalizeThemeData(row.data) : null;
  } catch {
    publishedTheme = null;
  }
  refreshGameThemePresentation();
  notifyThemeMusicMaybeChanged();
}

export function getPublishedThemeSnapshot(): ThemeData | null {
  return publishedTheme ? normalizeThemeData(publishedTheme) : null;
}

export function clearPublishedThemeLocal(): void {
  publishedTheme = null;
  refreshGameThemePresentation();
  notifyThemeMusicMaybeChanged();
}

/** For Theme Creator live colour preview without full decorations. */
export function previewThemeColorsOnly(colors: ThemeColors | null): void {
  if (!colors) {
    applyGameThemeColorOverrides();
    return;
  }
  const base = previewTheme || publishedTheme || emptyThemeData();
  setThemePreviewData({ ...base, colors: { ...colors } });
}
